import { api } from '@/lib/axios';

export type ModeHeuresSupEntrante = 'PRORATA' | 'FIXE';
export type ModeGasoilEntrante = 'DEDUIT' | 'LOUEUR' | 'MA_CHARGE';
/** Cause des heures perdues : les deux premières sont DÉDUITES au loueur, la dernière est facturée. */
export type MotifPerte = 'PANNE_LOUEUR' | 'INTEMPERIES' | 'ATTENTE_AWC';
export const MOTIF_LABEL: Record<MotifPerte, string> = {
  PANNE_LOUEUR: 'Panne / immobilisation du loueur (déduite)',
  INTEMPERIES: 'Intempéries — machine à l’arrêt (déduite)',
  ATTENTE_AWC: 'Attente imputable à AWC (facturée)',
};
/** Journée de référence : 08:00 → 16:00 = 8 h (la pause n'entre jamais dans le calcul). */
export const HEURE_DEBUT_STD = '08:00';
export const HEURE_FIN_STD = '16:00';
const minutes = (h: string) => Number(h.slice(0, 2)) * 60 + Number(h.slice(3, 5));
/** Aperçu local (le serveur fait foi) : heures perdues et heures sup d'une plage début → fin. */
export function apercuHoraire(debut: string, fin: string): { perdues: number; sup: number } | null {
  if (!debut || !fin || minutes(fin) <= minutes(debut)) return null;
  const d = minutes(debut) / 60, f = minutes(fin) / 60;
  const perdues = Math.min(8, Math.max(0, d - 8) + Math.max(0, 16 - f));
  const sup = Math.max(0, f - 16) + Math.max(0, 8 - d);
  return { perdues: Math.round(perdues * 100) / 100, sup: Math.round(sup * 100) / 100 };
}

export interface LigneDecompte {
  id: string;
  date: string;
  journees: string;
  heures_sup: string;
  heure_debut: string | null;
  heure_fin: string | null;
  motif_perte: MotifPerte | null;
  heures_perdues: string;
  gasoil_avance: string;
  gasoil_litres: string;
  mode_gasoil: ModeGasoilEntrante | null;
  chauffeur_nom: string | null;
  lieu: string | null;
  notes: string | null;
  montant: string;
  mode_gasoil_effectif: ModeGasoilEntrante;
}
export interface AvanceLoueur {
  id: string;
  date_avance: string;
  montant: string;
  mode_paiement: string | null;
  reference: string | null;
  notes: string | null;
}
export interface Decompte {
  id: string;
  reference: string | null;
  engin_id: string;
  loueur_id: string;
  chantier_id: string;
  periode_du: string | null;
  periode_au: string | null;
  tarif_jour: string;
  mode_heures_sup: ModeHeuresSupEntrante;
  tarif_heure_sup: string | null;
  mode_gasoil: ModeGasoilEntrante;
  chauffeur_nom: string | null;
  tarif_heure_applique: string;
  statut: string;
  facture_id: string | null;
  facture_reference: string | null;
  notes: string | null;
  lignes: LigneDecompte[];
  avances: AvanceLoueur[];
  total_brut: string;
  total_gasoil: string;
  total_avances: string;
  net_a_payer: string;
  /** Contrôle du gasoil À MA CHARGE (celui du loueur n'est pas enregistré). */
  gasoil_litres_total: string;
  heures_travaillees: string;
  conso_lh: string | null;
  conso_reference_lh: string | null;
  alerte_surconso: boolean;
  /** MES frais de gasoil : tout ce que j'ai versé, déduit au loueur ou non (≠ total_gasoil = part déduite). */
  gasoil_cout_total: string;
  gasoil_cout_par_heure: string | null;
  /** true = profil sans le droit « tarifs » : les montants sont neutralisés (0) par le serveur. */
  montants_masques: boolean;
}
interface Page<T> { items: T[]; total: number; page: number; size: number; pages: number }

export async function listDecomptes(enginId: string): Promise<Decompte[]> {
  const { data } = await api.get<Page<Decompte>>('/decomptes-location', { params: { engin_id: enginId, size: 200 } });
  return data.items;
}
export async function getDecompte(id: string): Promise<Decompte> {
  const { data } = await api.get<Decompte>(`/decomptes-location/${id}`);
  return data;
}
export async function createDecompte(payload: {
  engin_id: string; chantier_id: string; periode_du?: string; periode_au?: string;
  tarif_jour?: string | null; mode_heures_sup?: ModeHeuresSupEntrante;
  tarif_heure_sup?: string | null; mode_gasoil?: ModeGasoilEntrante;
  chauffeur_nom?: string | null; notes?: string;
}): Promise<Decompte> {
  const { data } = await api.post<Decompte>('/decomptes-location', payload);
  return data;
}
export async function addLigne(decompteId: string, payload: {
  date: string; journees: string; heures_sup?: string; gasoil_avance?: string;
  heure_debut?: string | null; heure_fin?: string | null; motif_perte?: MotifPerte | null;
  gasoil_litres?: string; mode_gasoil?: ModeGasoilEntrante | null;
  chauffeur_nom?: string; lieu?: string; notes?: string;
}): Promise<Decompte> {
  const { data } = await api.post<Decompte>(`/decomptes-location/${decompteId}/lignes`, payload);
  return data;
}
export async function deleteLigne(ligneId: string): Promise<void> {
  await api.delete(`/decomptes-location/lignes/${ligneId}`);
}
export async function addAvance(decompteId: string, payload: { date_avance: string; montant: string; reference?: string; notes?: string }): Promise<Decompte> {
  const { data } = await api.post<Decompte>(`/decomptes-location/${decompteId}/avances`, payload);
  return data;
}
export async function deleteAvance(avanceId: string): Promise<void> {
  await api.delete(`/decomptes-location/avances/${avanceId}`);
}
export async function validerDecompte(id: string): Promise<Decompte> {
  const { data } = await api.post<Decompte>(`/decomptes-location/${id}/valider`);
  return data;
}
export async function deleteDecompte(id: string): Promise<void> {
  await api.delete(`/decomptes-location/${id}`);
}

/** Fige le pointage SANS générer de facture (location non déclarée). */
export async function validerPointageDecompte(id: string): Promise<Decompte> {
  const { data } = await api.post<Decompte>(`/decomptes-location/${id}/valider-pointage`);
  return data;
}
/** Rouvre le pointage pour correction (supprime la facture si elle est encore au brouillon). */
export async function rouvrirPointageDecompte(id: string): Promise<Decompte> {
  const { data } = await api.post<Decompte>(`/decomptes-location/${id}/rouvrir`);
  return data;
}

/** Modifie l'en-tête d'un décompte ouvert (ex. changer le mode de gasoil). */
export async function updateDecompte(id: string, payload: { mode_gasoil?: ModeGasoilEntrante; chauffeur_nom?: string | null }): Promise<Decompte> {
  const { data } = await api.put<Decompte>(`/decomptes-location/${id}`, payload);
  return data;
}
